import React from 'react';
import { X, Plus, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';

export interface DishVariant { id: string; label: string; resolves_restrictions: string[]; }
export interface DishComponent {
    name: string;
    /** Composant retirable INDÉPENDAMMENT de l'ingrédient parent — ex: la châtaigne d'un "Toppings" peut se
     *  retirer sans perdre le cerfeuil ni la fleur de sel. Idée d'Emeni (2026-08-24) : "je veux deux niveaux
     *  de retirabilité". Si false, seul le retrait de TOUT l'ingrédient parent (s'il est lui-même retirable)
     *  permet d'écarter ce composant. */
    removable: boolean;
}
export interface DishIngredient {
    name: string;
    removable: boolean;
    /** De quoi la préparation est faite — "Sauce au beurre blanc" : [{name:"Beurre",...}, {name:"Vin blanc",...}].
     *  Un allergène porté par un composant est détecté sur le plat ; retirable avec la préparation entière,
     *  ou individuellement si le composant lui-même est marqué "possible de retirer". */
    components?: DishComponent[];
    /** Restrictions que CET ingrédient rend impossibles ("Lieu noir" -> végétarien). Les retirer tous rend
     *  le plat compatible : remplace le resolves_restrictions qu'il fallait saisir variante par variante. */
    incompatible_tags?: string[];
}

/** Sous-éditeurs de la fiche plat, sortis de MenuCatalogPage.tsx qui dépassait la limite de 500 lignes du
 *  projet (AI_DEVELOPER_RULES) et devenait difficile à relire d'un bloc. Aucun changement de comportement au
 *  passage — seul TracesEditor est nouveau. */

export function ChipListInput({ label, placeholder, values, onChange }: { label: string; placeholder: string; values: string[]; onChange: (v: string[]) => void }) {
    const [draft, setDraft] = React.useState('');
    const add = () => {
        const v = draft.trim();
        if (!v || values.includes(v)) { setDraft(''); return; }
        onChange([...values, v]);
        setDraft('');
    };
    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">{label}</label>
            <div className="flex items-center gap-1.5">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    placeholder={placeholder} className="flex-1" />
                <Button type="button" size="sm" variant="secondary" onClick={add}>Ajouter</Button>
            </div>
            {values.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {values.map((v) => (
                        <span key={v} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-surface-soft border border-border rounded-full pl-2.5 pr-1 py-1 text-content">
                            {v}
                            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400">
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// Éditeur d'ingrédients avec, pour chacun, une case "possible de retirer" — permet à la cuisine de savoir si
// elle peut composer l'assiette sans cet ingrédient pour un convive allergique, plutôt qu'une simple liste de
// mots-clés. Demande explicite d'Emeni (2026-08-23) : "dans les ingrédients, on doit pouvoir sélectionner
// ceux qu'on peut retirer".
export function IngredientEditor({ ingredients, onChange, knownRestrictions = [], allergensOf, allergenNames = [], onLinkAllergen }: {
    ingredients: DishIngredient[];
    onChange: (v: DishIngredient[]) => void;
    knownRestrictions?: string[];
    /** Allergènes qu'un ingrédient déclenche réellement — vide = il ne déclenchera rien. */
    allergensOf?: (i: DishIngredient) => string[];
    allergenNames?: string[];
    /** Rattache un ingrédient inconnu à un allergène, sans quitter la fiche. */
    onLinkAllergen?: (ingredient: string, allergen: string) => Promise<void>;
}) {
    const [draft, setDraft] = React.useState('');
    const [openName, setOpenName] = React.useState<string | null>(null);
    const [componentDraft, setComponentDraft] = React.useState('');

    const add = () => {
        const name = draft.trim();
        if (!name || ingredients.some((i) => i.name.toLowerCase() === name.toLowerCase())) { setDraft(''); return; }
        onChange([...ingredients, { name, removable: false, components: [], incompatible_tags: [] }]);
        setDraft('');
    };
    const remove = (name: string) => onChange(ingredients.filter((i) => i.name !== name));
    const patch = (name: string, p: Partial<DishIngredient>) =>
        onChange(ingredients.map((i) => i.name === name ? { ...i, ...p } : i));

    const addComponent = (i: DishIngredient) => {
        const v = componentDraft.trim();
        const current = i.components || [];
        if (!v || current.some((c) => c.name.toLowerCase() === v.toLowerCase())) { setComponentDraft(''); return; }
        patch(i.name, { components: [...current, { name: v, removable: false }] });
        setComponentDraft('');
    };
    const toggleComponentRemovable = (i: DishIngredient, componentName: string) => {
        const current = i.components || [];
        patch(i.name, { components: current.map((c) => c.name === componentName ? { ...c, removable: !c.removable } : c) });
    };
    const removeComponent = (i: DishIngredient, componentName: string) => {
        const current = i.components || [];
        patch(i.name, { components: current.filter((c) => c.name !== componentName) });
    };
    const toggleIncompatible = (i: DishIngredient, r: string) => {
        const current = i.incompatible_tags || [];
        patch(i.name, { incompatible_tags: current.includes(r) ? current.filter((x) => x !== r) : [...current, r] });
    };

    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Ingrédients</label>
            <p className="text-[10px] text-content-muted">
                Réellement présents dans l'assiette. Coche « possible de retirer » pour qu'un convive puisse demander le plat sans —
                une préparation comme « sauce au beurre blanc » se retire d'un bloc. Un ingrédient non retirable bloque le choix d'un convive allergique.
            </p>
            <div className="flex items-center gap-1.5">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    placeholder="Ex: Sauce au beurre blanc" className="flex-1" />
                <Button type="button" size="sm" variant="secondary" onClick={add}>Ajouter</Button>
            </div>
            {ingredients.length > 0 && (
                <div className="space-y-1">
                    {ingredients.map((i) => {
                        const comps = i.components || [];
                        const inc = i.incompatible_tags || [];
                        const open = openName === i.name;
                        return (
                            <div key={i.name} className="bg-surface-soft border border-border rounded-lg px-2.5 py-1.5 space-y-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <button type="button" onClick={() => { setOpenName(open ? null : i.name); setComponentDraft(''); }}
                                        className="flex items-center gap-1 text-xs font-semibold text-content min-w-0 hover:text-brand-600 dark:hover:text-brand-400">
                                        {open ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
                                        <span className="truncate">{i.name}</span>
                                        {comps.length > 0 && <span className="text-[9px] text-content-muted shrink-0">({comps.length})</span>}
                                        {/* Un ingrédient que le système ne reconnaît pas ne déclenchera AUCUNE alerte,
                                            quoi qu'il contienne en vrai — un "Boudoir" est plein de gluten mais reste un
                                            mot inconnu. Signalé ici pour être traité à la saisie, pas découvert par un
                                            convive non prévenu. Demande d'Emeni (2026-08-23). */}
                                        {allergensOf && allergensOf(i).length === 0 && (
                                            <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-black uppercase px-1 py-0.5 rounded bg-amber-100 text-amber-800" title="Aucun allergène détecté sur cet ingrédient">
                                                <AlertTriangle size={9} /> à vérifier
                                            </span>
                                        )}
                                        {allergensOf && allergensOf(i).length > 0 && (
                                            <span className="shrink-0 text-[9px] text-content-muted truncate">{allergensOf(i).join(', ')}</span>
                                        )}
                                    </button>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <label className="flex items-center gap-1 text-[10px] text-content-muted cursor-pointer">
                                            <input type="checkbox" checked={i.removable} onChange={() => patch(i.name, { removable: !i.removable })} className="w-3.5 h-3.5 rounded border-border accent-brand-600" />
                                            Possible de retirer
                                        </label>
                                        <button type="button" onClick={() => remove(i.name)} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400"><X size={11} /></button>
                                    </div>
                                </div>

                                {comps.length > 0 && !open && (
                                    <div className="flex flex-wrap gap-1 pl-4">
                                        {comps.map((cpt) => (
                                            <span key={cpt.name} className={`text-[9px] rounded-full px-1.5 py-0.5 border ${cpt.removable ? 'bg-brand-500/10 border-brand-500/20 text-brand-600 dark:text-brand-400' : 'bg-surface border-border text-content-muted'}`}>
                                                {cpt.removable && '✓ '}{cpt.name}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {open && (
                                    <div className="pl-4 space-y-2 pb-1">
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Composé de</p>
                                            <p className="text-[10px] text-content-muted">
                                                Détaille une préparation pour que ses allergènes soient détectés — « Beurre », « Vin blanc »… Inutile pour un ingrédient simple.
                                                Coche « possible de retirer » sur un composant précis (ex: « châtaigne ») pour le retirer SEUL, sans perdre le reste de la préparation.
                                            </p>
                                            {comps.length > 0 && (
                                                <div className="space-y-1">
                                                    {comps.map((cpt) => (
                                                        <div key={cpt.name} className="flex items-center justify-between gap-2 bg-surface border border-border rounded-lg pl-2 pr-1 py-1">
                                                            <span className="text-[11px] font-semibold text-content truncate">{cpt.name}</span>
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                <label className="flex items-center gap-1 text-[9px] text-content-muted cursor-pointer">
                                                                    <input type="checkbox" checked={cpt.removable} onChange={() => toggleComponentRemovable(i, cpt.name)} className="w-3 h-3 rounded border-border accent-brand-600" />
                                                                    Possible de retirer
                                                                </label>
                                                                <button type="button" onClick={() => removeComponent(i, cpt.name)} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400"><X size={9} /></button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1.5">
                                                <Input value={componentDraft} onChange={(e) => setComponentDraft(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addComponent(i); } }}
                                                    placeholder="Ex: Vin blanc" className="flex-1" />
                                                <Button type="button" size="sm" variant="secondary" onClick={() => addComponent(i)}>Ajouter</Button>
                                            </div>
                                        </div>

                                        {/* Aucune case "empêche telle restriction" à cocher : le système le déduit
                                            seul du nom et des composants. "Poisson" empêche « sans poisson » et
                                            « végétarien », « Vin blanc » empêche « halal », « Beurre » empêche
                                            « sans lactose » — c'était une saisie fastidieuse qui n'apprenait rien
                                            au système. Retour d'Emeni (2026-08-23) : "il faut que ce soit
                                            intelligent sans que j'aie à trop sélectionner de choses". */}

                                        {/* Rattachement direct, proposé au moment où l'on constate le manque —
                                            plus court que d'aller le faire dans l'écran des allergènes. */}
                                        {allergensOf && allergensOf(i).length === 0 && onLinkAllergen && allergenNames.length > 0 && (
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-widest">Aucun allergène détecté</p>
                                                <p className="text-[10px] text-content-muted">
                                                    Cet ingrédient ne déclenchera aucune alerte. Soit il est inoffensif (citron, salade…),
                                                    soit il faut le rattacher : « Boudoir » contient du gluten sans que le système puisse le
                                                    deviner. Détaille-le en composants ci-dessus, ou rattache-le directement — le
                                                    rattachement vaut alors pour tous tes plats.
                                                </p>
                                                <div className="flex flex-wrap gap-1">
                                                    {allergenNames.map((a) => (
                                                        <button key={a} type="button" onClick={() => onLinkAllergen(i.name, a)}
                                                            className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-border text-content-muted hover:text-content hover:border-brand-500">
                                                            + {a}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Les 14 allergènes réglementaires (règlement INCO 1169/2011) proposés en un clic — les traces relèvent
// presque toujours de cette liste fermée, et les retaper à la main pour 20 plats produirait des variantes
// ("Fruits à coques", "fruit à coque") qui casseraient la correspondance avec l'allergie déclarée par le
// convive, comparée en texte exact. La saisie libre reste possible pour un cas hors liste.
const REGULATORY_ALLERGENS = [
    'Gluten', 'Crustacés', 'Œuf', 'Poisson', 'Arachides', 'Soja', 'Lait', 'Fruits à coque',
    'Céleri', 'Moutarde', 'Sésame', 'Sulfites', 'Lupin', 'Mollusques',
];

// "Peut contenir des traces de" — contamination croisée inévitable en cuisine (même plan de travail, même
// huile de friture), par opposition aux ingrédients réellement présents. Côté convive, une trace PRÉVIENT
// sans jamais bloquer, là où un ingrédient non retirable bloque. Demande explicite d'Emeni (2026-08-23) :
// "quand il sélectionne ça ne doit pas les bloquer, par contre ça doit les prévenir des traces possibles
// inévitables".
export function TracesEditor({ traces, onChange }: { traces: string[]; onChange: (v: string[]) => void }) {
    const [draft, setDraft] = React.useState('');
    const has = (v: string) => traces.some((t) => t.toLowerCase() === v.toLowerCase());
    const toggle = (v: string) => onChange(has(v) ? traces.filter((t) => t.toLowerCase() !== v.toLowerCase()) : [...traces, v]);
    const addCustom = () => {
        const v = draft.trim();
        if (!v || has(v)) { setDraft(''); return; }
        onChange([...traces, v]);
        setDraft('');
    };
    const custom = traces.filter((t) => !REGULATORY_ALLERGENS.some((a) => a.toLowerCase() === t.toLowerCase()));

    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Peut contenir des traces de</label>
            <p className="text-[10px] text-content-muted">Contamination croisée possible en cuisine. Le convive allergique est prévenu, mais son choix n'est jamais bloqué.</p>
            <div className="flex flex-wrap gap-1.5">
                {REGULATORY_ALLERGENS.map((a) => (
                    <button key={a} type="button" onClick={() => toggle(a)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${has(a) ? 'bg-sky-600 border-sky-600 text-white' : 'border-border text-content-muted hover:text-content'}`}>
                        {a}
                    </button>
                ))}
            </div>
            <div className="flex items-center gap-1.5">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                    placeholder="Autre allergène non listé..." className="flex-1" />
                <Button type="button" size="sm" variant="secondary" onClick={addCustom}>Ajouter</Button>
            </div>
            {custom.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {custom.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-sky-500/10 border border-sky-500/20 rounded-full pl-2.5 pr-1 py-1 text-sky-700 dark:text-sky-300">
                            {t}
                            <button type="button" onClick={() => onChange(traces.filter((x) => x !== t))} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400">
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// Sélection multiple parmi les restrictions déjà connues du catalogue (dietary_tags + incompatible_tags
// existants sur tous les plats) — plutôt qu'un champ texte libre où l'établissement retape chaque fois le
// même mot avec un risque de faute/variante ("sans lactose" vs "sans-lactose"). On peut cocher plusieurs
// restrictions à la fois. Une restriction non encore connue reste ajoutable via le petit champ texte, pour ne
// jamais bloquer un cas nouveau. Demande explicite d'Emeni (2026-08-22) : "la variante doit être
// sélectionnable parmi celles déjà existantes [...] on doit pouvoir en sélectionner plusieurs".
export function RestrictionMultiSelect({ known, values, onChange }: { known: string[]; values: string[]; onChange: (v: string[]) => void }) {
    const [customDraft, setCustomDraft] = React.useState('');
    const toggle = (r: string) => onChange(values.includes(r) ? values.filter((x) => x !== r) : [...values, r]);
    const addCustom = () => {
        const v = customDraft.trim();
        if (!v) return;
        if (!values.includes(v)) onChange([...values, v]);
        setCustomDraft('');
    };
    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Résout les restrictions</label>
            {known.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {known.map((r) => (
                        <button key={r} type="button" onClick={() => toggle(r)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${values.includes(r) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                            {r}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex items-center gap-1.5">
                <Input value={customDraft} onChange={(e) => setCustomDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                    placeholder="Autre restriction non listée..." className="flex-1" />
                <Button type="button" size="sm" variant="secondary" onClick={addCustom}>Ajouter</Button>
            </div>
            {values.filter((v) => !known.includes(v)).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {values.filter((v) => !known.includes(v)).map((v) => (
                        <span key={v} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-surface-soft border border-border rounded-full pl-2.5 pr-1 py-1 text-content">
                            {v}
                            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400">
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// Éditeur de variantes intégré à la fiche du plat lui-même — ex: pour "Milanaise de volaille", des variantes
// "Sans volaille" et "Sans volaille et sans sauce", chacune reliée aux restrictions qu'elle résout. Remplace
// l'ancien système de "plat alternatif" en fiche séparée (dupliquée, avec ses propres ingrédients/allergènes)
// qu'Emeni trouvait trop compliqué à gérer et confus dans la liste. Demande explicite d'Emeni (2026-08-22) :
// "moi je le mettrais directement dans la fiche du plat, une section alternative où on viendrait mettre des
// codes comme les sans lactose etc [...] pour les alternatives je dois les relier à des restrictions déjà
// connues".
export function VariantEditor({ variants, onChange, knownRestrictions }: { variants: DishVariant[]; onChange: (v: DishVariant[]) => void; knownRestrictions: string[] }) {
    const [labelDraft, setLabelDraft] = React.useState('');
    const [restrictionsDraft, setRestrictionsDraft] = React.useState<string[]>([]);

    const addVariant = () => {
        const label = labelDraft.trim();
        if (!label) return;
        onChange([...variants, { id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label, resolves_restrictions: restrictionsDraft }]);
        setLabelDraft('');
        setRestrictionsDraft([]);
    };
    const removeVariant = (id: string) => onChange(variants.filter((v) => v.id !== id));

    return (
        <div className="space-y-2">
            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Variantes (ex: "Sans viande", "Sans viande et sans sauce")</label>
            {variants.length > 0 && (
                <div className="space-y-1.5">
                    {variants.map((v) => (
                        <div key={v.id} className="flex items-start justify-between gap-2 bg-surface-soft border border-border rounded-lg px-2.5 py-2">
                            <div>
                                <p className="text-xs font-bold text-content">{v.label}</p>
                                {v.resolves_restrictions.length > 0 && (
                                    <p className="text-[10px] text-content-muted mt-0.5">Résout : {v.resolves_restrictions.join(', ')}</p>
                                )}
                            </div>
                            <button type="button" onClick={() => removeVariant(v.id)} className="p-1 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400 shrink-0"><X size={13} /></button>
                        </div>
                    ))}
                </div>
            )}
            <div className="bg-surface border border-dashed border-border rounded-lg p-2.5 space-y-2">
                <Input value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)} placeholder="Nom de la variante (ex: Sans viande et sans sauce)" />
                <RestrictionMultiSelect known={knownRestrictions} values={restrictionsDraft} onChange={setRestrictionsDraft} />
                <Button type="button" size="sm" variant="secondary" onClick={addVariant} disabled={!labelDraft.trim()}>
                    <Plus size={12} /> Ajouter cette variante
                </Button>
            </div>
        </div>
    );
}
